import { useQuery } from '@tanstack/react-query';
import { useMatches } from '@tanstack/react-router';
import {
  getGetInterviewInviteQueryOptions,
  getGetInterviewTypeQueryOptions,
  getGetCandidateQueryOptions,
} from '@sara/api-client';
import { HeaderBreadcrumbs, type ResolvedCrumb } from '../../components/header-breadcrumbs';
import type { CrumbConfig, StaticCrumbLink } from '../../router/types';

export function HeaderBreadcrumbsContent() {
  const matches = useMatches();

  const sources = matches
    .filter((m) => Boolean(m.staticData?.crumb))
    .map((m) => ({
      config: m.staticData.crumb as CrumbConfig,
      params: m.params as Record<string, unknown>,
      matchId: m.id,
    }));

  // Look up entity ids once so query hooks stay stable across renders.
  const interviewId = sources.find((s) => s.config.kind === 'interview')?.params.interviewId as
    | string
    | undefined;
  const interviewTypeId = sources.find((s) => s.config.kind === 'interviewType')?.params
    .interviewTypeId as string | undefined;
  const candidateId = sources.find((s) => s.config.kind === 'candidate')?.params.candidateId as
    | string
    | undefined;

  const interviewQuery = useQuery({
    ...getGetInterviewInviteQueryOptions(interviewId ?? ''),
    enabled: Boolean(interviewId),
    staleTime: 60_000,
  });
  if (interviewQuery.error) throw interviewQuery.error;

  const interviewTypeQuery = useQuery({
    ...getGetInterviewTypeQueryOptions(interviewTypeId ?? ''),
    enabled: Boolean(interviewTypeId),
    staleTime: 60_000,
  });
  if (interviewTypeQuery.error) throw interviewTypeQuery.error;

  const candidateQuery = useQuery({
    ...getGetCandidateQueryOptions(candidateId ?? ''),
    enabled: Boolean(candidateId),
    staleTime: 60_000,
  });
  if (candidateQuery.error) throw candidateQuery.error;

  const crumbs = resolveCrumbs(sources, {
    interview: interviewQuery,
    interviewType: interviewTypeQuery,
    candidate: candidateQuery,
  });

  return <HeaderBreadcrumbs crumbs={crumbs} />;
}

interface CrumbSource {
  config: CrumbConfig;
  params: Record<string, unknown>;
  matchId: string;
}

interface DynamicQueries {
  interview: ReturnType<typeof useQuery>;
  interviewType: ReturnType<typeof useQuery>;
  candidate: ReturnType<typeof useQuery>;
}

function applySlug(to: string | undefined, slug: string | undefined): string | undefined {
  if (!to) return to;
  if (!slug) return to.includes(':orgSlug') ? undefined : to;
  return to.replace(':orgSlug', slug);
}

function resolveCrumbs(sources: CrumbSource[], queries: DynamicQueries): ResolvedCrumb[] {
  if (sources.length === 0) return [];

  const orgSlug = sources
    .map((s) => s.params.orgSlug)
    .find((v): v is string => typeof v === 'string');

  const out: ResolvedCrumb[] = [];

  sources.forEach(({ config, matchId }) => {
    appendAncestors(out, config.parent, matchId, orgSlug);

    switch (config.kind) {
      case 'static':
        out.push({
          key: matchId,
          label: config.label,
          to: applySlug(config.to, orgSlug),
          isCurrent: false,
          isLoading: false,
        });
        break;
      case 'interview': {
        const data = queries.interview.data as
          | { candidate: { firstName: string; lastName: string; email: string } }
          | undefined;
        const label = data
          ? `${data.candidate.firstName} ${data.candidate.lastName}`.trim() || data.candidate.email
          : null;
        out.push({
          key: matchId,
          label,
          isCurrent: false,
          isLoading: queries.interview.isPending && queries.interview.fetchStatus !== 'idle',
        });
        break;
      }
      case 'interviewType': {
        const data = queries.interviewType.data as { name: string } | undefined;
        out.push({
          key: matchId,
          label: data?.name ?? null,
          isCurrent: false,
          isLoading:
            queries.interviewType.isPending && queries.interviewType.fetchStatus !== 'idle',
        });
        break;
      }
      case 'candidate': {
        const data = queries.candidate.data as { firstName: string; lastName: string } | undefined;
        const label = data ? `${data.firstName} ${data.lastName}`.trim() || null : null;
        out.push({
          key: matchId,
          label,
          isCurrent: false,
          isLoading: queries.candidate.isPending && queries.candidate.fetchStatus !== 'idle',
        });
        break;
      }
    }
  });

  if (out.length > 0) {
    out[out.length - 1].isCurrent = true;
  }
  return out;
}

function appendAncestors(
  out: ResolvedCrumb[],
  parent: StaticCrumbLink | undefined,
  matchId: string,
  orgSlug: string | undefined
) {
  if (!parent) return;
  appendAncestors(out, parent.parent, matchId, orgSlug);
  const resolvedTo = applySlug(parent.to, orgSlug);
  if (out.some((c) => c.label === parent.label && c.to === resolvedTo)) return;
  out.push({
    key: `${matchId}-${parent.label}`,
    label: parent.label,
    to: resolvedTo,
    isCurrent: false,
    isLoading: false,
  });
}
